// How a conversation shows in the list, as the app does it
// (app/.../conversation/ConversationRepository.kt, ui/conversation/ConversationListScreen.kt).

import type { ConversationResponse, UserSummaryResponse } from "../api/contract.gen.ts";
import type { StoredMessage } from "./store.ts";

/** Voxie, the agent everyone has (app: VOXIE_USER_ID). */
export const VOXIE_ID = "00000000-0000-4000-8000-000000000a6e";

export function label(user: UserSummaryResponse | undefined): string {
  if (!user) return "Unbekannt";
  return user.display_name ?? (user.username ? `@${user.username}` : "Gelöschtes Konto");
}

export function isSelf(c: ConversationResponse): boolean {
  return c.kind === "self" || (c.kind === "direct" && c.members.length === 1);
}

/** The person the conversation shows: the partner, yourself in the self chat, Voxie in the agent chat. */
export function partner(c: ConversationResponse, me: string | null): UserSummaryResponse | undefined {
  if (c.kind === "group") return undefined;
  if (isSelf(c)) return c.members[0]?.user;
  return c.members.find((m) => m.user.id !== me)?.user;
}

export function title(c: ConversationResponse, me: string | null): string {
  if (c.kind === "group") return c.title ?? "Gruppe";
  if (c.kind === "agent") return "Voxie";
  if (isSelf(c)) return "Selbstgespräch";
  return label(partner(c, me));
}

export function subtitle(c: ConversationResponse, me: string | null, online: Set<string>): string {
  if (c.kind === "group") {
    const names = c.members.filter((m) => m.user.id !== me).map((m) => label(m.user).split(" ")[0]);
    return [...names, "du"].join(", ");
  }
  if (c.kind === "agent" || isSelf(c)) return "";
  const p = partner(c, me);
  return p && online.has(p.id) ? "online" : "";
}

export function duration(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Markdown reduced to plain text, whitespace collapsed, at most 200 characters. */
export function plain(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__|\*|_|~~)(.+?)\1/g, "$2")
    .replace(/^\s{0,3}(#{1,6}\s|>\s?|[-*+]\s|\d+\.\s)/gm, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}

/** The line under a conversation's title in the list. */
export function preview(c: ConversationResponse, me: string | null, last: StoredMessage | undefined): string {
  const m = c.last_message;
  if (!m) return "Noch keine Nachrichten";
  const sender = m.sender_id === me ? "Du: " : c.kind === "group" ? `${label(c.members.find((x) => x.user.id === m.sender_id)?.user).split(" ")[0]}: ` : "";
  const prefix = isSelf(c) ? "" : sender;
  const caption = last?.content.text ? plain(last.content.text) : "";
  const withCaption = (word: string, extra = caption) => (extra ? `${word} · ${extra}` : word);
  switch (m.kind) {
    case "deleted":
      return `${prefix}Nachricht gelöscht`;
    case "voice":
      return `${prefix}🎤 ${last?.response.transcript?.text ? plain(last.response.transcript.text) : duration(m.duration_ms)}`;
    case "image":
      return prefix + withCaption("Foto");
    case "video":
      return prefix + withCaption("Video");
    case "file":
      return prefix + withCaption("Datei", caption || last?.content.fileName || "");
    case "location":
      return prefix + withCaption("📍 Standort", last?.content.place?.label ?? "");
    case "gif":
      return prefix + withCaption("GIF", last?.content.gif?.title ?? "");
    default:
      return prefix + (caption || "Nachricht");
  }
}

/** The list order: self chat and Voxie on top, then pinned, then by last activity. */
export function sortConversations(list: ConversationResponse[]): ConversationResponse[] {
  const rank = (c: ConversationResponse) => (isSelf(c) ? 0 : c.kind === "agent" ? 1 : c.pinned ? 2 : 3);
  return [...list].sort((a, b) => rank(a) - rank(b) || b.last_activity_at.localeCompare(a.last_activity_at));
}

/** "10:24", "gestern", "Mo", "12.10." like a chat list. */
export function shortTime(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(now) - day(d)) / 86_400_000);
  if (days === 0) return d.toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" });
  if (days === 1) return "gestern";
  if (days < 7) return d.toLocaleDateString("de-AT", { weekday: "short" }).replace(".", "");
  return d.toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit" });
}
